import React, { useState } from 'react';
import { getCurrencySymbol } from '@shared/constants/billing.js';
import { getAppUrl } from '@shared/config/domains.js';
import { captureError } from '@/utils/errorHandler';

export default function AdminUsageBillingSection({ 
  app,
  appId,
  authFetch,
  showToast,
  onUpdate
}) {
  const [loading, setLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  
  const webhookUrl = `${getAppUrl(true)}/api/apps/report-usage`;

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const response = await authFetch('/api/admin/usage-billing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId, action }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update usage billing');
      }

      const actionMessages = {
        'enable': '✅ Usage billing enabled',
        'disable': '✅ Usage billing disabled',
        'regenerate-secret': '✅ Usage secret regenerated',
      };
      showToast(actionMessages[action], 'success');
      
      if (onUpdate) onUpdate();
    } catch (error) {
      captureError(error, {
        action: 'handleAction',
        component: 'AdminUsageBillingSection',
        extra: { appId, action },
      });
      console.error('Error updating usage billing:', error);
      showToast(error.message || 'Failed to update usage billing', 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    showToast(`✅ ${label} copied to clipboard`, 'success');
  };

  const usageReports = app?.usageReports || [];
  const hasUsageBilling = app?.hasUsageBilling || false;
  const usageSecret = app?.usageSecret || null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">📊 Usage Billing</h2>
      
      {/* Status */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
        <div>
          <span className="text-sm text-gray-600">Status:</span>
          <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            hasUsageBilling 
              ? 'bg-green-100 text-green-700' 
              : 'bg-gray-100 text-gray-600'
          }`}>
            {hasUsageBilling ? '✅ Enabled' : '⚪ Disabled'}
          </span>
        </div>
        <button
          onClick={() => handleAction(hasUsageBilling ? 'disable' : 'enable')}
          disabled={loading}
          className={`px-4 py-2 rounded-lg font-medium text-sm disabled:opacity-50 ${
            hasUsageBilling
              ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          {loading ? 'Updating...' : hasUsageBilling ? 'Disable' : 'Enable'}
        </button>
      </div>

      {/* Configuration (only show when enabled) */}
      {hasUsageBilling && (
        <>
          {/* Setup Guide Toggle */}
          <div className="mb-4">
            <button
              onClick={() => setShowSetupGuide(!showSetupGuide)}
              className="flex items-center gap-2 text-sm font-medium text-zapt-purple hover:text-zapt-purple/80"
            >
              <span>{showSetupGuide ? '▼' : '▶'}</span>
              <span>Setup Guide</span>
            </button>
          </div>

          {/* Expanded Setup Guide */}
          {showSetupGuide && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">How to Set Up Usage Billing</h3>
              
              <div className="space-y-4 text-sm text-blue-800">
                {/* Step 1 */}
                <div>
                  <p className="font-medium">1. Add Environment Variables</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Copy the values below and add them to the deployed app's environment variables (e.g., in Vercel or the app's <code className="bg-blue-100 px-1 rounded">.env</code> file):
                  </p>
                  <ul className="text-xs text-blue-700 mt-1 ml-4 list-disc space-y-1">
                    <li><code className="bg-blue-100 px-1 rounded">ZAPT_WEBHOOK_URL</code> - The webhook endpoint</li>
                    <li><code className="bg-blue-100 px-1 rounded">ZAPT_APP_ID</code> - This app's ID</li>
                    <li><code className="bg-blue-100 px-1 rounded">ZAPT_USAGE_SECRET</code> - Secret for authentication</li>
                  </ul>
                </div>

                {/* Step 2 */}
                <div>
                  <p className="font-medium">2. Add a Trigger.dev Scheduled Task</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Create a scheduled task in the deployed app that runs on the <strong>1st of each month</strong>. ZAPT generates invoices on the 2nd, with a retry on the 3rd if needed. The task should:
                  </p>
                  <ul className="text-xs text-blue-700 mt-1 ml-4 list-disc space-y-1">
                    <li>Count active users (or calculate usage based on the app's pricing model)</li>
                    <li>Calculate the total cost for the previous month</li>
                    <li>Send a POST request to ZAPT with the amount and description</li>
                  </ul>
                </div>

                {/* Step 3 */}
                <div>
                  <p className="font-medium">3. Example Code for Deployed App</p>
                  <p className="text-xs text-blue-700 mt-1 mb-2">
                    Add this file to the deployed app's <code className="bg-blue-100 px-1 rounded">trigger/</code> folder:
                  </p>
                  <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-lg overflow-x-auto">
{`// trigger/report-usage-to-zapt.ts
import { schedules } from "@trigger.dev/sdk";
import { db } from "../drizzle/client";
import { users } from "../drizzle/schema";
import { eq, sql } from "drizzle-orm";

export const reportUsageTask = schedules.task({
  id: "report-usage-to-zapt",
  // Run on 1st of each month at 9am UTC
  // ZAPT generates invoices on the 2nd, with retry on the 3rd
  cron: { pattern: "0 9 1 * *" },
  run: async () => {
    // Count active users
    const result = await db
      .select({ count: sql\`count(*)::int\` })
      .from(users)
      .where(eq(users.status, 'active'));
    
    const userCount = result[0]?.count || 0;
    const perUserFee = 5.00; // Your per-user price
    const amount = userCount * perUserFee;

    // Calculate billing period (previous month)
    const now = new Date();
    const periodStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const periodEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    // Report to ZAPT
    await fetch(process.env.ZAPT_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Usage-Secret': process.env.ZAPT_USAGE_SECRET,
      },
      body: JSON.stringify({
        appId: process.env.ZAPT_APP_ID,
        periodStart: periodStart.toISOString().split('T')[0],
        periodEnd: periodEnd.toISOString().split('T')[0],
        amount,
        description: \`\${userCount} active users × $\${perUserFee}/user\`,
      }),
    });
  },
});`}
                  </pre>
                </div>

                {/* Step 4 */}
                <div>
                  <p className="font-medium">4. Deploy and Test</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Deploy the app to Trigger.dev (<code className="bg-blue-100 px-1 rounded">npx trigger.dev@latest deploy</code>). 
                    You can manually trigger the task in the Trigger.dev dashboard to test it - look for the "Test" button on the task page. 
                    Once successful, you'll see the usage report appear in the table below.
                  </p>
                  <p className="text-xs text-blue-600 mt-2 italic">
                    Tip: Test the task manually first before waiting for the scheduled run on the 1st.
                  </p>
                </div>

                {/* How it works */}
                <div className="pt-3 border-t border-blue-200">
                  <p className="font-medium">How Billing Works</p>
                  <ul className="text-xs text-blue-700 mt-1 ml-4 list-disc space-y-1">
                    <li><strong>1st of month, 9am:</strong> Apps report usage for the previous month</li>
                    <li><strong>2nd of month, 9am:</strong> ZAPT generates invoices</li>
                    <li><strong>If usage missing:</strong> App is skipped entirely (no partial charge), alert email sent</li>
                    <li><strong>3rd of month, 9am:</strong> Retry - if late report arrived, full invoice created (maintenance + usage)</li>
                  </ul>
                  <p className="text-xs text-blue-600 mt-2 italic">
                    Apps are billed atomically: either the full charge goes through, or nothing. No split invoices.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Environment Variables */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-3">Environment Variables for Deployed App:</p>
            
            {/* Webhook URL */}
            <div className="mb-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-600">ZAPT_WEBHOOK_URL</label>
                <button
                  onClick={() => copyToClipboard(webhookUrl, 'Webhook URL')}
                  className="text-xs text-zapt-purple hover:text-zapt-purple/80 underline"
                >
                  Copy
                </button>
              </div>
              <div className="mt-1 p-2 bg-white border border-gray-200 rounded font-mono text-xs text-gray-800 break-all">
                {webhookUrl}
              </div>
            </div>
            
            {/* App ID */}
            <div className="mb-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-600">ZAPT_APP_ID</label>
                <button
                  onClick={() => copyToClipboard(appId, 'App ID')}
                  className="text-xs text-zapt-purple hover:text-zapt-purple/80 underline"
                >
                  Copy
                </button>
              </div>
              <div className="mt-1 p-2 bg-white border border-gray-200 rounded font-mono text-xs text-gray-800 break-all">
                {appId}
              </div>
            </div>

            {/* Usage Secret */}
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-600">ZAPT_USAGE_SECRET</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowSecret(!showSecret)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    {showSecret ? 'Hide' : 'Show'}
                  </button>
                  {usageSecret && (
                    <button
                      onClick={() => copyToClipboard(usageSecret, 'Usage Secret')}
                      className="text-xs text-zapt-purple hover:text-zapt-purple/80 underline"
                    >
                      Copy
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-1 p-2 bg-white border border-gray-200 rounded font-mono text-xs text-gray-800 break-all">
                {showSecret ? (usageSecret || 'No secret generated') : '••••••••••••••••••••••••••••••••••••'}
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => handleAction('regenerate-secret')}
                  disabled={loading}
                  className="text-xs text-red-600 hover:text-red-700 underline disabled:opacity-50"
                >
                  Regenerate Secret
                </button>
                <span className="text-xs text-gray-400">
                  (regenerating will require updating the deployed app)
                </span>
              </div>
            </div>
          </div>

          {/* Usage Reports */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">Recent Usage Reports</h3>
              {usageReports.length > 0 && (
                <span className="text-xs text-gray-500">{usageReports.length} report{usageReports.length !== 1 ? 's' : ''}</span>
              )}
            </div>
            {usageReports.length > 0 ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reported</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {usageReports.map((report) => (
                      <tr key={report.id}>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {formatDate(report.periodStart)} - {formatDate(report.periodEnd)}
                        </td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900">
                          {getCurrencySymbol(app?.preferredCurrency || 'usd')}{parseFloat(report.amount).toFixed(2)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate" title={report.description}>
                          {report.description || '-'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">
                          {formatDateTime(report.reportedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 font-medium">No usage reports received yet</p>
                <p className="text-xs text-yellow-700 mt-1">
                  Reports will appear here once the deployed app sends them. Click "Setup Guide" above for instructions.
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Info when disabled */}
      {!hasUsageBilling && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800 font-medium mb-2">What is Usage Billing?</p>
          <p className="text-xs text-blue-700 mb-3">
            Usage billing allows the deployed app to automatically report monthly costs based on actual usage. 
            This is useful for pricing models like:
          </p>
          <ul className="text-xs text-blue-700 ml-4 list-disc space-y-1 mb-3">
            <li><strong>Per-user pricing</strong> - charge based on number of active users (e.g., $5/user/month)</li>
            <li><strong>Per-transaction pricing</strong> - charge based on number of orders, bookings, etc.</li>
            <li><strong>Tiered pricing</strong> - different rates based on usage volume</li>
            <li><strong>Hybrid pricing</strong> - flat maintenance fee + usage charges</li>
          </ul>
          <p className="text-xs text-blue-700">
            <strong>How it works:</strong> The deployed app reports its monthly cost on the 1st of each month. 
            This amount is automatically added to the customer's invoice alongside any fixed maintenance fees.
          </p>
          <div className="mt-3 pt-3 border-t border-blue-200">
            <p className="text-xs text-blue-600">
              Click "Enable" above to set up usage billing for this app.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    hour: '2-digit', 
    minute: '2-digit' 
  });
}
